import { createPortal } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import { MoveMenu } from '../ui/controls';
import { useFlag } from '../shell/hooks';
import { useNotice } from '../shell/notice';
import { useBookEdits } from '../book/actions';
import { loadBook } from '../book/load';
import { moveChoices, type MoveTo } from '../book/changes';
import { AddEntry, FinishSheet, Sheet as SheetFrame, MovePlaceSheet, StartSheet, StopSheet, type StopChoice } from '../book/reading';
import { LoanSteps } from '../book/library';
import { INTEREST_HINTS } from '../book/changes';
import { currentLoan, openTargets } from '../book/model';
import { InterestDots } from '../ui/bits';
import { libraryNeedsYou, pauseCheckIn, type ShelfBook } from './model';
import { NotForMeSheet } from '../book/BookView';
import { PastReadSheet } from '../book/edit';
import type { BookData, Read } from '../book/types';

// The buttons on a shelf card (Kristi, Oct 28: Move ▾ and Start reading in Part 1). They are the
// book page's own actions and sheets, with the same Undo: the book is read in full only when a
// button is used, so a shelf of cards stays quick.

type Sheet =
  | { kind: 'start' }
  | { kind: 'stop'; to?: StopChoice }
  | { kind: 'finish' }
  | { kind: 'past' }
  | { kind: 'place'; to: 'up_next' | 'future_reads' }
  | { kind: 'not_for_me' }
  | { kind: 'note' }
  | { kind: 'didnt_finish'; copy: string };

type Pending =
  | { sheet: Sheet }
  | { move: MoveTo }
  | { act: 'read_pause' | 'read_resume' | 'read_pause_later'; message: string }
  | { interest: number | null }
  | { ready: true };

export function CardActions({ b, today, phone }: { b: ShelfBook; today: string; phone: boolean }) {
  const notice = useNotice();
  const editOn = useFlag('book_page.edit');
  const [book, setBook] = useState<BookData | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [busy, setBusy] = useState(false);
  const edits = useBookEdits(book, setBook, () => loadBook(b.label));

  const reading = Boolean(b.reading);
  const paused = Boolean(b.reading?.pausedOn);
  const checkIn = paused ? pauseCheckIn(b.reading, today) : null;
  // A library step that needs you: the book page's own steps, so the full book is read straight
  // away for these few cards. On Up Next (or any card not being read): ready, borrowed, a hold to
  // check. On Currently Reading (Oct 29): a loan due within 3 days or ended (Returned…, Renew).
  const library = libraryNeedsYou(b.loan, reading, today);
  useEffect(() => {
    if (library && !book) go({ ready: true });
  }, [library]);
  // The library copy whose loan needs you (the one the shelf saw), else any library copy with a loan.
  const libCopy =
    library && book
      ? (book.copies.find((c) => c.how === 'library_loan' && currentLoan(c)?.id === b.loan?.id) ?? book.copies.find((c) => c.how === 'library_loan' && currentLoan(c)))
      : undefined;
  const targets = b.openIn ? openTargets({ ...b.openIn, work: { title: b.title } as BookData['work'], authors: b.author ? [b.author.replace(/ and others$/, '')] : [] }, phone) : [];

  /** Read the book fresh, then do what was asked. */
  const go = (p: Pending) => {
    setBusy(true);
    loadBook(b.label)
      .then((full) => {
        setBook(full);
        if (!('ready' in p)) setPending(p);
      })
      .catch((e: unknown) => notice({ message: `Couldn’t open ${b.title}: ${e instanceof Error ? e.message : String(e)}`, done: false }))
      .finally(() => setBusy(false));
  };

  // Once the book is here (and edits sees it), run the move or open the sheet.
  useEffect(() => {
    if (!book || !pending) return;
    const p = pending;
    setPending(null);
    if ('sheet' in p) setSheet(p.sheet);
    else if ('move' in p) void edits.move(p.move);
    else if ('interest' in p) void edits.set('interest', p.interest);
    else if ('act' in p) void edits.act(p.act, {}, p.message);
  }, [book, pending]);

  const shape = { status: b.status, reads: reading ? ([{ outcome: 'open' }] as unknown as Read[]) : [] } as Pick<BookData, 'status' | 'reads'>;
  // Finished… with nothing to open (no read, edit mode off) is hidden, not explained (R11).
  const choices = moveChoices(shape, { pastRead: Boolean(editOn) }).filter((c) => !(c.label === 'Finished…' && !c.opens && !c.to));
  const pick = (label: string) => {
    const c = choices.find((x) => x.label === label);
    if (!c || c.here) return;
    if (c.opens === 'start') go({ sheet: { kind: 'start' } });
    else if (c.opens === 'finish') go({ sheet: { kind: 'finish' } });
    else if (c.opens === 'past') go({ sheet: { kind: 'past' } });
    else if (c.opens === 'stop') go({ sheet: { kind: 'stop', to: c.stopTo } });
    else if (!c.to) notice({ message: `${c.label.replace('…', '')}: ${c.note}.`, done: false });
    else if (c.to === 'not_for_me') go({ sheet: { kind: 'not_for_me' } });
    else if (c.to === 'up_next' || c.to === 'future_reads') go({ sheet: { kind: 'place', to: c.to } });
    else go({ move: c.to });
  };
  const close = () => setSheet(null);

  return (
    <>
      {b.status === 'up_next' ? (
        <span class="card-interest">
          <InterestDots value={b.interest} hints={INTEREST_HINTS} onChange={(v) => go({ interest: v })} />
        </span>
      ) : null}
      {checkIn ? (
        <div class="heads-up card-checkin" role="group" aria-label="Still reading?">
          <span>
            Paused {checkIn.weeks} weeks · still reading?
          </span>
          <span class="card-checkin-buttons">
            <button type="button" class="btn-book" disabled={busy} onClick={() => go({ act: 'read_resume', message: `Resumed ${b.title}` })}>
              Resume reading
            </button>
            <button type="button" class="btn-secondary" disabled={busy} onClick={() => go({ sheet: { kind: 'stop', to: 'future_reads' } })}>
              Try again later
            </button>
            <button type="button" class="btn-secondary" disabled={busy} onClick={() => go({ sheet: { kind: 'stop', to: 'not_for_me' } })}>
              Not for me
            </button>
            <button type="button" class="btn-link" disabled={busy} onClick={() => go({ act: 'read_pause_later', message: `${b.title} stays paused; asks again in 4 weeks` })}>
              Leave paused for now
            </button>
          </span>
        </div>
      ) : null}
      {library && book && libCopy ? (
        <LoanSteps
          book={book}
          copy={libCopy}
          edits={edits}
          onStart={() => setSheet({ kind: 'start' })}
          onFinished={() => setSheet({ kind: 'finish' })}
          onDidntFinish={() => setSheet({ kind: 'didnt_finish', copy: libCopy.id })}
          stopNearby={reading}
        />
      ) : null}
      {targets.length ? (
        <span class="card-open-in">
          {targets.map((t) => (
            <a key={t.url} class="btn-book" href={t.url} target="_blank" rel="noopener noreferrer" title={t.sub}>
              {t.label}
            </a>
          ))}
        </span>
      ) : null}
      {reading ? (
        <>
          <button
            type="button"
            class="btn-book"
            disabled={busy}
            onClick={() => go({ act: paused ? 'read_resume' : 'read_pause', message: paused ? `Resumed ${b.title}` : `Paused ${b.title}` })}
          >
            {paused ? 'Resume reading' : 'Pause'}
          </button>
          <button type="button" class="btn-book" disabled={busy} onClick={() => go({ sheet: { kind: 'stop' } })}>
            Stop reading…
          </button>
          <button type="button" class="btn-secondary" disabled={busy} onClick={() => go({ sheet: { kind: 'note' } })}>
            + Reading note
          </button>
        </>
      ) : b.status === 'up_next' && !library ? (
        <button type="button" class="btn-main" disabled={busy} onClick={() => go({ sheet: { kind: 'start' } })}>
          Start reading
        </button>
      ) : null}
      {/* Move ▾: the shared one (ui/controls), ✓ before the shelf it's on (R4). */}
      <MoveMenu
        book={b.title}
        items={choices.map((c, i) => ({ label: c.label, note: c.here ? undefined : c.note || undefined, current: c.here, heading: i === 0 ? 'Move to' : undefined, onSelect: () => pick(c.label) }))}
      />
      {book && sheet
        ? createPortal(
            <div class="shelf-sheet">
              {sheet.kind === 'start' ? <StartSheet book={book} edits={edits} onClose={close} /> : null}
              {sheet.kind === 'stop' ? <StopSheet book={book} edits={edits} onClose={close} initial={sheet.to} /> : null}
              {sheet.kind === 'finish' ? <FinishSheet book={book} edits={edits} onClose={close} /> : null}
              {sheet.kind === 'past' ? <PastReadSheet book={book} edits={edits} onClose={close} move /> : null}
              {sheet.kind === 'place' ? <MovePlaceSheet book={book} to={sheet.to} edits={edits} onClose={close} /> : null}
              {sheet.kind === 'not_for_me' ? <NotForMeSheet book={book} edits={edits} onClose={close} /> : null}
              {sheet.kind === 'note' ? (
                <SheetFrame title={`Reading note · ${b.title}`} sub="Dated today, on the current read." onClose={close}>
                  <AddEntry kind="note" book={book} edits={edits} onClose={close} />
                </SheetFrame>
              ) : null}
              {sheet.kind === 'didnt_finish' ? <StopSheet book={book} edits={edits} onClose={close} loanCopy={sheet.copy} /> : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
